import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const TikTokScript = ({ prompt, data }) => {
  const { FiCopy, FiCheck, FiDownload, FiEdit, FiZap, FiVideo } = FiIcons;
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('script');

  // Provide fallback data if none exists
  const safeData = data || {
    location: 'Paradise Island',
    vibe: 'Luxe Travel',
    gender: 'Feminine',
    skinTone: 'Deep Ebony',
    hairType: '4C Coils',
    mobility: 'None',
    experience: 'Arrival Scene',
    pose: 'Relaxed and radiant',
    relationship: 'Solo',
    children: 'No Children'
  };

  const safePrompt = prompt || "Experience inclusive travel through the lens of a melanated traveler, captured confidently at a stunning destination.";

  const generateTikTokScript = () => {
    const hook = `POV: You're a ${safeData.gender?.toLowerCase() || 'melanated'} traveler with ${safeData.skinTone?.toLowerCase() || 'beautiful'} skin living your best life in ${safeData.location || 'paradise'} ✨`;
    
    const scenes = [
      {
        timestamp: "0-3s",
        scene: "Hook & Arrival",
        visual: `${safeData.gender || 'Traveler'} stepping into ${safeData.location || 'destination'} - ${safeData.experience || 'arrival moment'}`,
        text: hook,
        music: "Trending travel audio - upbeat"
      },
      {
        timestamp: "3-8s", 
        scene: "The Reveal",
        visual: `Showcasing ${safeData.vibe?.toLowerCase() || 'luxe'} accommodations and ${safeData.pose?.toLowerCase() || 'confident'} poses`,
        text: `This ${safeData.vibe?.toLowerCase() || 'luxury'} experience hits different when you're embracing your authentic self 🌍`,
        music: "Audio continues - emotional build"
      },
      {
        timestamp: "8-15s",
        scene: "Lifestyle Shots", 
        visual: `${safeData.hairType || 'Natural hair'} flowing, ${safeData.skinTone?.toLowerCase() || 'melanated'} skin glowing in natural light`,
        text: `Your ${safeData.hairType?.toLowerCase() || 'natural hair'} and unique beauty deserve to be celebrated in every destination 👑`,
        music: "Peak emotional moment"
      },
      {
        timestamp: "15-20s",
        scene: "Inclusive Message",
        visual: safeData.mobility !== 'None' ? `Highlighting ${safeData.mobility?.toLowerCase()} accessibility` : 'Diverse representation in travel',
        text: safeData.mobility !== 'None' ? `Travel is for EVERYONE - accessibility and inclusion matter ♿️` : `Representation matters in every frame 📸`,
        music: "Inspiring, uplifting"
      },
      {
        timestamp: "20-25s",
        scene: "Call to Action",
        visual: `Wide shot of ${safeData.location || 'destination'} with overlay text`,
        text: `Who's ready to book their inclusive ${safeData.vibe?.toLowerCase() || 'travel'} experience? ✈️ Drop a 🌴 below!`,
        music: "Strong finish beat"
      }
    ];

    return { hook, scenes };
  };

  const script = generateTikTokScript();

  const hashtags = [
    `#${safeData.location?.replace(/\s+/g, '') || 'Travel'}`,
    '#MelanatedTravel',
    '#BlackTravelFeed', 
    '#TravelWhileBlack',
    '#InclusiveTravel',
    `#${safeData.vibe?.replace(/\s+/g, '') || 'LuxeTravel'}`,
    '#TravelTok',
    '#Representation',
    '#AuthenticTravel',
    '#DiverseTravel'
  ];

  const copyScript = async () => {
    const fullScript = `🎬 TIKTOK SCRIPT: ${safeData.location || 'Travel'} Content

🎯 HOOK: ${script.hook}

📋 SCENE BREAKDOWN:
${script.scenes.map(scene => 
  `${scene.timestamp} - ${scene.scene}
Visual: ${scene.visual}
Text/Voiceover: "${scene.text}"
Music: ${scene.music}
`).join('\n')}

🏷️ HASHTAGS: ${hashtags.join(' ')}

💡 CONTENT NOTES:
- Focus on authentic representation
- Highlight ${safeData.vibe?.toLowerCase() || 'luxury'} aspects
- Show inclusive travel experience
- Encourage engagement and bookings`;

    try {
      await navigator.clipboard.writeText(fullScript);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy script: ', err);
    }
  };

  const downloadScript = () => {
    const fullScript = `TikTok Script: ${safeData.location || 'Travel'} Content\n\n${script.scenes.map(scene => `${scene.timestamp}: ${scene.text}\n`).join('\n')}\n\nHashtags: ${hashtags.join(' ')}`;
    
    const element = document.createElement('a');
    const file = new Blob([fullScript], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `tiktok-script-${safeData.location?.replace(/\s+/g, '-').toLowerCase() || 'travel'}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const viralHooks = [
    `POV: You're the type of ${safeData.gender?.toLowerCase() || 'person'} that deserves ${safeData.vibe?.toLowerCase() || 'luxury'} travel`,
    `Tell me you're ready for ${safeData.location || 'paradise'} without telling me you're ready`,
    `When people say ${safeData.vibe?.toLowerCase() || 'luxury travel'} isn't for people who look like us...`,
    `Nobody talks about what it's REALLY like being a melanated traveler in ${safeData.location || 'destinations'}`,
    `Here's what happened when I showed up with my ${safeData.hairType?.toLowerCase() || 'natural hair'} and ${safeData.skinTone?.toLowerCase() || 'beautiful'} skin`
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-gray-800 rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold text-yellow-400">TikTok Script Generator</h2>
          <div className="flex items-center space-x-3">
            <button
              onClick={copyScript}
              className="bg-blue-500 hover:bg-blue-400 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <SafeIcon icon={copied ? FiCheck : FiCopy} />
              <span>{copied ? 'Copied!' : 'Copy Script'}</span>
            </button>
            <button
              onClick={downloadScript}
              className="bg-green-500 hover:bg-green-400 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <SafeIcon icon={FiDownload} />
              <span>Download</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700 mb-6">
          <button
            onClick={() => setActiveTab('script')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'script' 
                ? 'text-yellow-400 border-b-2 border-yellow-400' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            Script Breakdown
          </button>
          <button
            onClick={() => setActiveTab('hooks')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'hooks' 
                ? 'text-yellow-400 border-b-2 border-yellow-400' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            Viral Hooks
          </button>
          <button
            onClick={() => setActiveTab('tips')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'tips' 
                ? 'text-yellow-400 border-b-2 border-yellow-400' 
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            Content Tips
          </button>
        </div>

        {activeTab === 'script' && (
          <div className="space-y-6">
            {/* Hook Section */}
            <div className="bg-gradient-to-r from-purple-900 to-pink-800 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
                <SafeIcon icon={FiZap} className="mr-2 text-yellow-400" />
                Opening Hook (0-3s)
              </h3>
              <p className="text-white text-lg font-medium">{script.hook}</p>
              <p className="text-gray-200 text-sm mt-2">🎯 Grabs attention in first 3 seconds</p>
            </div>

            {/* Scene Breakdown */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-white mb-4">Scene by Scene Breakdown</h3>
              {script.scenes.map((scene, index) => (
                <div key={index} className="bg-gray-700 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-yellow-400">{scene.timestamp} - {scene.scene}</h4>
                    <SafeIcon icon={FiVideo} className="text-blue-400" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-semibold text-gray-300 mb-2">Visual</h5>
                      <p className="text-gray-100 text-sm">{scene.visual}</p>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-300 mb-2">Text/Voiceover</h5>
                      <p className="text-gray-100 text-sm italic">"{scene.text}"</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <h5 className="font-semibold text-gray-300 mb-1">Music</h5>
                    <p className="text-gray-400 text-xs">{scene.music}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Hashtags */}
            <div className="bg-gray-700 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-white mb-4">Recommended Hashtags</h3>
              <div className="flex flex-wrap gap-2">
                {hashtags.map((tag, index) => (
                  <span key={index} className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'hooks' && (
          <div className="space-y-6">
            <div className="bg-gray-700 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
                <SafeIcon icon={FiZap} className="mr-2 text-yellow-400" />
                Alternative Viral Hooks
              </h3>
              <div className="space-y-4">
                {viralHooks.map((hook, index) => (
                  <div key={index} className="bg-gray-800 rounded-lg p-4 border border-gray-600">
                    <p className="text-gray-100 font-medium">{hook}</p>
                    <p className="text-gray-400 text-sm mt-1">Hook option #{index + 1}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'tips' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-700 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-4">Content Strategy</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>• Hook viewers in first 3 seconds</li>
                  <li>• Show authentic representation</li>
                  <li>• Include trending audio</li>
                  <li>• Use text overlays for key points</li>
                  <li>• End with clear call-to-action</li>
                </ul>
              </div>
              <div className="bg-gray-700 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-4">Visual Tips</h3>
                <ul className="space-y-2 text-gray-300">
                  <li>• Good lighting for {safeData.skinTone?.toLowerCase() || 'melanated'} skin</li>
                  <li>• Showcase {safeData.hairType?.toLowerCase() || 'natural hair'} texture</li>
                  <li>• Include {safeData.location || 'destination'} landmarks</li>
                  <li>• Show accessibility features if applicable</li>
                  <li>• Use vertical 9:16 format</li>
                </ul>
              </div>
            </div>

            <div className="bg-gray-700 rounded-lg p-6">
              <h3 className="text-xl font-semibold text-white mb-4">Engagement Optimization</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-800 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-400 mb-2">Post Timing</h4>
                  <p className="text-gray-300 text-sm">Peak hours: 6-10am, 7-9pm EST</p>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-400 mb-2">Captions</h4>
                  <p className="text-gray-300 text-sm">Ask questions, use emojis, include CTAs</p>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <h4 className="font-semibold text-yellow-400 mb-2">Trends</h4>
                  <p className="text-gray-300 text-sm">Use trending sounds and hashtags</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TikTokScript;